import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Popconfirm,
  Space,
  Typography,
  Card,
  Spin,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import ContractHeader from '../Contract/components/Navigation/ContractHeader';
import { getBadgeForm, createAttendee, updateAttendee, deleteAttendee } from './services/badgeApi';
import './styles.css';

const { Title, Text, Link } = Typography;
const { Option } = Select;

const TITLE_OPTIONS = [
  'President / CEO',
  'Vice President',
  'VP Sales',
  'VP Marketing',
  'National Sales Manager',
  'Regional Sales Manager',
  'Territory Sales Manager',
  'Account Executive',
  'Sales Representative',
  'Marketing Manager',
  'Brand Manager',
  'Product Manager',
  'Director of Sales',
  'Director of Marketing',
  'Broker / Manufacturer Rep',
  'Other',
];

const YN_OPTIONS = [
  { label: 'Yes (Y)', value: 'Y' },
  { label: 'No (N)', value: 'N' },
];

const YN_FLAGS = [
  { name: 'dinnerEvent',    label: 'Dinner Event' },
  { name: 'settingUp',      label: 'Setting Up' },
  { name: 'breakingDown',   label: 'Breaking Down' },
  { name: 'workingShow',    label: 'Working Show' },
  { name: 'mfrRep',         label: 'Mfr Rep' },
];

const DEFAULT_YN = { dinnerEvent: 'N', settingUp: 'N', breakingDown: 'N', workingShow: 'N', mfrRep: 'N' };

const BadgeForm = () => {
  const { vendorCode } = useParams();
  const navigate = useNavigate();

  const [vendorName, setVendorName] = useState('');
  const [showYear, setShowYear]     = useState('');
  const [attendees, setAttendees]   = useState([]);
  const [loading, setLoading]       = useState(true);

  const [modalOpen, setModalOpen]           = useState(false);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [modalLoading, setModalLoading]     = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [form] = Form.useForm();

  // Auto-save refs (mirrors contract step pattern)
  const autoSaveTimerRef    = useRef(null);
  const hasChangedRef       = useRef(false);
  const latestFormValuesRef = useRef(null);

  // ── Data fetch ──────────────────────────────────────────────────────────────

  const fetchBadgeForm = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBadgeForm(vendorCode);
      setVendorName(data.vendorName || '');
      setShowYear(data.showYear || '');
      setAttendees(
        (data.attendees || []).map((a) => ({ ...a, key: a.badgeId }))
      );
    } catch {
      message.error('Failed to load badge form');
    } finally {
      setLoading(false);
    }
  }, [vendorCode]);

  useEffect(() => {
    fetchBadgeForm();
  }, [fetchBadgeForm]);

  // ── Auto-save (edit mode only) ───────────────────────────────────────────────

  const flushAutoSave = useCallback(async () => {
    if (!editingAttendee || !hasChangedRef.current || !latestFormValuesRef.current) return;
    try {
      const values = latestFormValuesRef.current;
      await updateAttendee(vendorCode, editingAttendee.badgeId, values);
      setAttendees((prev) =>
        prev.map((a) => (a.badgeId === editingAttendee.badgeId ? { ...a, ...values } : a))
      );
      hasChangedRef.current = false;
    } catch {
      // silent — user will see error on explicit save
    }
  }, [editingAttendee, vendorCode]);

  const scheduleAutoSave = useCallback(
    (allValues) => {
      if (!editingAttendee) return;
      hasChangedRef.current    = true;
      latestFormValuesRef.current = allValues;
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(flushAutoSave, 3000);
    },
    [editingAttendee, flushAutoSave]
  );

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const openAddModal = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    hasChangedRef.current    = false;
    latestFormValuesRef.current = null;
    setEditingAttendee(null);
    form.resetFields();
    form.setFieldsValue({ ...DEFAULT_YN, tabletRental: 0, scannerRental: 0 });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    hasChangedRef.current    = false;
    latestFormValuesRef.current = null;
    setEditingAttendee(record);
    form.setFieldsValue({
      firstName:    record.firstName,
      lastName:     record.lastName,
      title:        record.title   || undefined,
      email:        record.email,
      mobileNumber: record.mobileNumber || '',
      dinnerEvent:  record.dinnerEvent  || 'N',
      settingUp:    record.settingUp    || 'N',
      breakingDown: record.breakingDown || 'N',
      workingShow:  record.workingShow  || 'N',
      mfrRep:       record.mfrRep       || 'N',
      tabletRental:  record.tabletRental  ?? 0,
      scannerRental: record.scannerRental ?? 0,
    });
    setModalOpen(true);
  };

  const handleModalClose = async () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    if (editingAttendee && hasChangedRef.current) {
      await flushAutoSave();
    }
    setModalOpen(false);
    setEditingAttendee(null);
    form.resetFields();
  };

  // ── Submit (add or explicit save) ────────────────────────────────────────────

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // validation error shown inline
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    hasChangedRef.current = false;
    setModalLoading(true);
    try {
      if (editingAttendee) {
        await updateAttendee(vendorCode, editingAttendee.badgeId, values);
        setAttendees((prev) =>
          prev.map((a) =>
            a.badgeId === editingAttendee.badgeId ? { ...a, ...values } : a
          )
        );
        message.success('Attendee updated');
      } else {
        const created = await createAttendee(vendorCode, values);
        setAttendees((prev) => [...prev, { ...created, key: created.badgeId }]);
        message.success('Attendee added');
      }
      setModalOpen(false);
      setEditingAttendee(null);
      form.resetFields();
    } catch (err) {
      const detail =
        err?.response?.data?.email?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.detail ||
        'Failed to save attendee';
      message.error(detail);
    } finally {
      setModalLoading(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const handleDelete = async (record) => {
    setDeleteLoadingId(record.badgeId);
    try {
      await deleteAttendee(vendorCode, record.badgeId);
      setAttendees((prev) => prev.filter((a) => a.badgeId !== record.badgeId));
      message.success('Attendee removed');
    } catch {
      message.error('Failed to remove attendee');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────────

  const columns = [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Mobile',
      dataIndex: 'mobileNumber',
      key: 'mobileNumber',
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Dinner',
      dataIndex: 'dinnerEvent',
      key: 'dinnerEvent',
      align: 'center',
      render: (v) => <Tag color={v === 'Y' ? 'green' : 'default'}>{v || 'N'}</Tag>,
    },
    {
      title: 'Setting Up',
      dataIndex: 'settingUp',
      key: 'settingUp',
      align: 'center',
      render: (v) => <Tag color={v === 'Y' ? 'green' : 'default'}>{v || 'N'}</Tag>,
    },
    {
      title: 'Breaking Down',
      dataIndex: 'breakingDown',
      key: 'breakingDown',
      align: 'center',
      render: (v) => <Tag color={v === 'Y' ? 'green' : 'default'}>{v || 'N'}</Tag>,
    },
    {
      title: 'Working Show',
      dataIndex: 'workingShow',
      key: 'workingShow',
      align: 'center',
      render: (v) => <Tag color={v === 'Y' ? 'green' : 'default'}>{v || 'N'}</Tag>,
    },
    {
      title: 'Mfr Rep',
      dataIndex: 'mfrRep',
      key: 'mfrRep',
      align: 'center',
      render: (v) => <Tag color={v === 'Y' ? 'green' : 'default'}>{v || 'N'}</Tag>,
    },
    {
      title: 'Tablets',
      dataIndex: 'tabletRental',
      key: 'tabletRental',
      align: 'center',
    },
    {
      title: 'Scan Guns',
      dataIndex: 'scannerRental',
      key: 'scannerRental',
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this attendee?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Remove"
            okType="danger"
          >
            <Tooltip title="Remove">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoadingId === record.badgeId}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="badge-page">
      <ContractHeader />

      <div className="badge-content">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          className="badge-back-btn"
          onClick={() => navigate('/welcome')}
        >
          Back to Portal
        </Button>

        <div className="badge-page-header">
          <div>
            <Title level={2} className="badge-title">
              Badge Form
            </Title>
            <Text className="badge-subtitle">
              {showYear} Open House — {vendorName}{' '}
              <span className="badge-vendor-code">({vendorCode})</span>
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAddModal}
            size="large"
            className="badge-add-btn"
          >
            Add Attendee
          </Button>
        </div>

        <Card className="badge-card">
          {loading ? (
            <div className="badge-loading">
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={attendees}
              size="middle"
              scroll={{ x: 'max-content' }}
              pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
              locale={{
                emptyText: 'No attendees added yet. Click "Add Attendee" to get started.',
              }}
            />
          )}
        </Card>

        <div className="badge-support">
          <Text type="secondary">
            Need help? Contact{' '}
            <Link href="mailto:itsupport@arett.com">itsupport@arett.com</Link>
          </Text>
        </div>
      </div>

      {/* ── Add / Edit Modal ─────────────────────────────────────────────────── */}
      <Modal
        title={editingAttendee ? 'Edit Attendee' : 'Add Attendee'}
        open={modalOpen}
        onCancel={handleModalClose}
        onOk={handleSubmit}
        okText={editingAttendee ? 'Save Changes' : 'Add Attendee'}
        confirmLoading={modalLoading}
        width={640}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="badge-form"
          onValuesChange={(_, allValues) => scheduleAutoSave(allValues)}
        >
          {/* Row 1: Name */}
          <div className="badge-form-row">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: 'First name is required' }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: 'Last name is required' }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </div>

          {/* Row 2: Title + Email */}
          <div className="badge-form-row">
            <Form.Item
              name="title"
              label="Title / Job Title"
              rules={[{ required: true, message: 'Title is required' }]}
            >
              <Select placeholder="Select a title" showSearch allowClear>
                {TITLE_OPTIONS.map((t) => (
                  <Option key={t} value={t}>{t}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input placeholder="email@example.com" />
            </Form.Item>
          </div>

          {/* Row 3: Mobile (full width) */}
          <Form.Item name="mobileNumber" label="Mobile Number">
            <Input placeholder="+1 (555) 000-0000" />
          </Form.Item>

          {/* Row 4: Y/N flags */}
          <div className="badge-form-flags">
            {YN_FLAGS.map(({ name, label }) => (
              <Form.Item key={name} name={name} label={label}>
                <Select options={YN_OPTIONS} />
              </Form.Item>
            ))}
          </div>

          {/* Row 5: Equipment */}
          <div className="badge-form-row">
            <Form.Item
              name="tabletRental"
              label="# of Tablets Requested"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="scannerRental"
              label="# of Scan Guns Requested"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BadgeForm;
